import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { MENU, MENU_LINK } from "../data/menu";

const Tag = ({ label }) => (
  <span
    className={`ml-3 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${
      label === "Vegan" ? "bg-[#78825B]/15 text-[#5c6644]" : "bg-[#4B7F94]/10 text-[#4B7F94]"
    }`}
  >
    {label}
  </span>
);

const Item = ({ item }) => (
  <li className="py-4" data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="flex items-baseline">
      <span className="text-lg font-semibold text-[#1A1C19]">
        {item.name}
        {item.tag && <Tag label={item.tag} />}
      </span>
      <span className="dot-leader" />
      <span className="font-display whitespace-nowrap text-xl tracking-tight text-[#1A1C19]">
        {item.price} €
      </span>
    </div>
    {item.desc && <p className="mt-1.5 max-w-xl text-base leading-relaxed text-[#3D403A]">{item.desc}</p>}
  </li>
);

export const MenuSection = () => (
  <section id="speisekarte" data-testid="menu-section" className="relative z-10 py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-6 text-xs font-bold uppercase tracking-[0.28em] text-[#78825B]"
          >
            Speisekarte
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl font-light leading-none tracking-tight text-[#1A1C19] sm:text-5xl lg:text-6xl"
          >
            Frisch vom Grill,{" "}
            <span className="italic text-[#4B7F94]">täglich selbstgemacht</span>
          </motion.h2>
        </div>
        <motion.a
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          href={MENU_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="menu-online-link"
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#4B7F94] transition-colors duration-300 hover:text-[#1A1C19]"
        >
          Online bestellen auf m.tsolias.de
          <ExternalLink size={14} strokeWidth={1.5} />
        </motion.a>
      </div>

      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <aside className="md:col-span-4 lg:col-span-3">
          <div className="sticky top-24">
            <nav className="flex flex-wrap gap-2 md:flex-col md:gap-0" data-testid="menu-category-nav">
              {MENU.map((cat) => (
                <a
                  key={cat.id}
                  href={`#cat-${cat.id}`}
                  data-testid={`menu-category-link-${cat.id}`}
                  className="rounded-full border border-[#E2E0D8] bg-white/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#4A4D46] transition-all duration-300 hover:border-[#4B7F94] hover:text-[#4B7F94] md:rounded-none md:border-0 md:border-l-2 md:border-[#E2E0D8] md:bg-transparent md:px-5 md:py-2.5 md:hover:border-[#4B7F94]"
                >
                  {cat.name}
                </a>
              ))}
            </nav>
            <div className="mt-10 hidden space-y-6 md:block">
              <motion.img
                initial={{ opacity: 0, y: 24, rotate: 3 }}
                whileInView={{ opacity: 1, y: 0, rotate: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                src="https://images.unsplash.com/photo-1603360946369-dc9bb6258143?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxneXJvcyUyMG1lYXQlMjBwbGF0ZXxlbnwwfHx8fDE3ODgwMTQ4MzN8MA&ixlib=rb-4.1.0&q=85"
                alt="Gyros-Teller mit Pommes und Pita"
                data-testid="menu-food-image-1"
                className="h-44 w-full rounded-2xl border border-[#E2E0D8] object-cover shadow-[0_20px_50px_rgba(26,28,25,0.10)] lg:h-56"
              />
              <motion.img
                initial={{ opacity: 0, y: 24, rotate: -3 }}
                whileInView={{ opacity: 1, y: 0, rotate: -1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                src="https://images.unsplash.com/photo-1625944230945-1b7dd3b949ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGdyZWVrJTIwc2FsYWR8ZW58MHx8fHwxNzg4MDE0ODM4fDA&ixlib=rb-4.1.0&q=85"
                alt="Frischer griechischer Salat"
                data-testid="menu-food-image-2"
                className="h-44 w-full rounded-2xl border border-[#E2E0D8] object-cover shadow-[0_20px_50px_rgba(26,28,25,0.10)] lg:h-56"
              />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#78825B]">
                Gyros, Souvlaki & frische Salate — täglich selbstgemacht
              </p>
            </div>
          </div>
        </aside>

        <div className="md:col-span-7 md:col-start-6 lg:col-span-8 lg:col-start-5">
          {MENU.map((cat, ci) => (
            <motion.div
              key={cat.id}
              id={`cat-${cat.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="mb-16 scroll-mt-28"
              data-testid={`menu-category-${cat.id}`}
            >
              <h3 className="font-display mb-4 flex items-baseline gap-4 text-2xl tracking-tight text-[#1A1C19] md:text-3xl">
                <span className="text-sm italic text-[#78825B]">{String(ci + 1).padStart(2, "0")}</span>
                {cat.name}
              </h3>
              {cat.groups ? (
                cat.groups.map((g) => (
                  <div key={g.label} className="mb-6">
                    <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.22em] text-[#78825B]">{g.label}</p>
                    <ul className="divide-y divide-[#E2E0D8]/70">
                      {g.items.map((item) => (
                        <Item key={item.name + g.label} item={item} />
                      ))}
                    </ul>
                  </div>
                ))
              ) : (
                <ul className="divide-y divide-[#E2E0D8]/70">
                  {cat.items.map((item) => (
                    <Item key={item.name} item={item} />
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
